import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { paymentErrorMessage } from '../paymentErrors';

// Client "Request payout" modal — wallet CASH only (bonus is never
// withdrawable). The requested amount is reserved (not spendable) while the
// payout is pending; the owner pays manually by bank transfer and marks it paid,
// which is when the wallet is actually debited.
//
// props:
//   withdrawable — max cash the client can request right now
//   onClose / onSuccess — onSuccess receives the created payout row
export default function RequestPayoutModal({ walletClientId = null, withdrawable, onClose, onSuccess }) {
    const [amount, setAmount] = useState('');
    const [iban, setIban] = useState('');
    const [accountName, setAccountName] = useState('');
    const [bankName, setBankName] = useState('');
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // Previewed unused bonus that would be clawed back for the entered amount.
    const [bonusRemoved, setBonusRemoved] = useState(0);

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const maxAmount = Math.max(0, Number(withdrawable) || 0);
    const parsedAmount = parseFloat(amount);
    const cleanIban = iban.replace(/\s+/g, '').toUpperCase();

    // Withdrawing wallet cash also removes the UNUSED bonus that was earned on
    // that cash (PLAN_payments.md — "Bonus clawback on withdrawal"). Task A's
    // backend exposes GET /api/payouts/preview?amount=… → { bonus_removed, … };
    // we preview it (debounced) so the client is warned before submitting.
    // Advisory only: any failure just hides the note — submit stays allowed.
    const previewSeq = useRef(0);
    useEffect(() => {
        if (isNaN(parsedAmount) || parsedAmount <= 0 || parsedAmount > maxAmount + 0.005) {
            setBonusRemoved(0);
            return;
        }
        const seq = ++previewSeq.current;
        const timer = setTimeout(async () => {
            try {
                const res = await axios.get('/api/payouts/preview', { params: { amount: parsedAmount, ...(walletClientId ? { wallet: walletClientId } : {}) } });
                if (seq !== previewSeq.current) return; // superseded by a newer amount
                const removed = Number(res.data?.bonus_removed) || 0;
                setBonusRemoved(removed > 0 ? removed : 0);
            } catch (err) {
                if (seq !== previewSeq.current) return;
                setBonusRemoved(0);
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [parsedAmount, maxAmount]);

    const validate = () => {
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            return 'Enter a payout amount greater than zero.';
        }
        if (parsedAmount > maxAmount + 0.005) {
            return `You can withdraw up to ${money(maxAmount)} (wallet cash only — bonus credit is not withdrawable).`;
        }
        if (cleanIban.length < 15) {
            return 'Enter a valid IBAN.';
        }
        if (!accountName.trim()) {
            return 'Enter the bank account holder name.';
        }
        return '';
    };

    const handleSubmit = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            const res = await axios.post('/api/payouts', {
                wallet_client_id: walletClientId || undefined,
                amount: parsedAmount,
                iban: cleanIban,
                account_name: accountName.trim(),
                bank_name: bankName.trim() || null,
                note: note.trim() || null,
            });
            toast.success('Payout requested — we will process it by bank transfer.');
            if (onSuccess) onSuccess(res.data);
        } catch (err) {
            console.error('Error requesting payout:', err);
            setError(paymentErrorMessage(err, 'Failed to request the payout. Please try again.'));
            setSubmitting(false);
        }
    };

    return (
        <div className="cwallet__depositOverlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="cwallet__depositModal">
                <button type="button" className="modal-x" onClick={onClose}>&times;</button>
                <h2 className="cwallet__depositTitle">Withdraw cash balance</h2>

                <div className="cwallet__payoutInfo">
                    Payouts are processed <strong>manually by bank transfer</strong>, usually within a few
                    business days. Only wallet <strong>cash</strong> can be withdrawn — bonus credit is
                    spend-only. The requested amount is set aside (not spendable) while your payout is
                    being processed, and you can cancel it any time before it is paid.
                </div>

                <div className="cwallet__customInput">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                        <label className="cwallet__inputLabel" style={{ marginBottom: 0 }}>Amount (AED) — up to {money(maxAmount)}</label>
                        {/* Fill the full withdrawable cash so the client never has
                            to copy the ceiling by hand (2026-07-24). */}
                        <button
                            type="button"
                            onClick={() => { setAmount(maxAmount.toFixed(2)); setError(''); }}
                            disabled={maxAmount <= 0}
                            style={{
                                border: '1px solid #ff5a60',
                                background: 'rgba(255,90,96,0.10)',
                                color: '#ff5a60',
                                fontWeight: 700,
                                fontSize: 12,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                padding: '4px 14px',
                                borderRadius: 999,
                                cursor: maxAmount <= 0 ? 'not-allowed' : 'pointer',
                                opacity: maxAmount <= 0 ? 0.45 : 1,
                                lineHeight: 1.4,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Max
                        </button>
                    </div>
                    <input
                        type="number"
                        className="cwallet__input"
                        placeholder={`Max ${maxAmount.toFixed(2)}`}
                        value={amount}
                        min="0"
                        max={maxAmount}
                        step="0.01"
                        onChange={(e) => { setAmount(e.target.value); setError(''); }}
                    />
                </div>

                {bonusRemoved > 0 && (
                    <div className="cwallet__payoutClawback">
                        Withdrawing {money(parsedAmount)} will also remove {money(bonusRemoved)} in unused bonus earned on that amount.
                    </div>
                )}

                <div className="cwallet__customInput" style={{ marginTop: 12 }}>
                    <label className="cwallet__inputLabel">IBAN *</label>
                    <input
                        type="text"
                        className="cwallet__input"
                        placeholder="AE00 0000 0000 0000 0000 000"
                        value={iban}
                        onChange={(e) => { setIban(e.target.value); setError(''); }}
                    />
                </div>

                <div className="cwallet__customInput" style={{ marginTop: 12 }}>
                    <label className="cwallet__inputLabel">Account Holder Name *</label>
                    <input
                        type="text"
                        className="cwallet__input"
                        placeholder="Name on the bank account"
                        value={accountName}
                        onChange={(e) => { setAccountName(e.target.value); setError(''); }}
                    />
                </div>

                <div className="cwallet__customInput" style={{ marginTop: 12 }}>
                    <label className="cwallet__inputLabel">Bank Name (optional)</label>
                    <input
                        type="text"
                        className="cwallet__input"
                        placeholder="e.g. Emirates NBD"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                    />
                </div>

                <div className="cwallet__customInput" style={{ marginTop: 12 }}>
                    <label className="cwallet__inputLabel">Note (optional)</label>
                    <textarea
                        className="cwallet__textarea"
                        placeholder="Anything we should know"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </div>

                {error && <div className="cwallet__amountError">{error}</div>}

                <div className="cwallet__depositActions">
                    <button className="cwallet__depositBtn cwallet__depositBtn--secondary" onClick={onClose} disabled={submitting}>
                        Cancel
                    </button>
                    <button
                        className="cwallet__depositBtn cwallet__depositBtn--primary"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? 'Submitting…' : 'Withdraw cash balance'}
                    </button>
                </div>
            </div>
        </div>
    );
}
